// External libraries
import { useCallback, useEffect, useState } from "react";
import { ulid } from "ulid";

// FontAwesome icons
import { faLock, faLockOpen, faPenLine, faTrashCan } from "@fortawesome/pro-duotone-svg-icons";
import { faFloppyDisk, faPlusLarge } from "@fortawesome/pro-solid-svg-icons";
import ColorChangingIcon from "../../Common/UI/ColorChangingIcon";

// Kendo React components
import { TabStrip, TabStripTab, TileLayout } from "@progress/kendo-react-layout";

// Common components and signals
import { notification } from "../../Common/Store/CommonSignals";
import { selectedHamburgerIndex } from "../../Navigation/HamburgerMenu/HamburgerMenuSignals";

// Styles
import "../Homepage.css";
import "./Homepage2.css";

// Local components
import ConfirmDeleteDashboardDialog from "./Dialogs/ConfirmDeleteDashboardDialog";
import ConfirmDeleteHomepageTileDialog from "./Dialogs/ConfirmDeleteHomepageTileDialog";
import HomepageTileDialog from "./Dialogs/HomepageTileDialog";
import TileConfigDialog from "./Dialogs/TileConfigDialog";
import TreeViewConfigDialog from "./Dialogs/TreeViewConfigDialog";
import { HomepageTreeView } from "./HomepageTreeView";

// Local services and signals
import { homepageService } from "./HomepageService";
import {
  configuringTileId,
  configuringTreeViewId,
  currentDashboardId,
  dashboardName,
  dashboardTabs,
  dashboardToDelete,
  editingTile,
  isEditingName,
  isLoading,
  selectedTabIndex,
  showDeleteDashboardDialog,
  showDeleteTileDialog,
  showTileConfigDialog,
  showTileDialog,
  showTreeViewConfigMenu,
  tileDialogMode,
  tileLayoutItems,
  tileToDelete,
  tileTreeViewConfigs,
} from "./HomepageSignals";

const HomePage2 = () => {
  selectedHamburgerIndex.value = 1;

  useEffect(() => {
    loadInitialTiles();
  }, []);

  const loadInitialTiles = async () => {
    try {
      isLoading.value = true;
      // Reset selected tab index to avoid showing first tab content on a different selected tab
      selectedTabIndex.value = 0;

      // Load all dashboards from backend
      const dashboards = await homepageService.loadAllDashboards();

      if (dashboards && dashboards.length > 0) {
        // Initialize tabs with loaded dashboards
        dashboardTabs.value = dashboards.map((dashboard) => ({
          id: dashboard.id,
          name: dashboard.name,
          tiles: dashboard.tiles,
          treeViewConfigs: dashboard.treeViewConfigs,
          isLocked: dashboard.isLocked ?? true,
        }));

        // Set first dashboard as current
        const firstDashboard = dashboards[0];
        tileLayoutItems.value = firstDashboard.tiles;
        tileTreeViewConfigs.value = firstDashboard.treeViewConfigs;
        currentDashboardId.value = firstDashboard.id;
        dashboardName.value = firstDashboard.name;
      } else {
        // No dashboards found - create default
        const defaultTileId = 1;
        const defaultTiles = [
          {
            id: defaultTileId,
            header: "Favorite Templates",
            body: "",
            defaultPosition: {
              col: 1,
              colSpan: 2,
              rowSpan: 4,
            },
            resizable: true,
            reorderable: true,
          },
        ];
        const defaultTreeViewConfigs = {
          [defaultTileId]: {
            treeViews: [
              { id: "tv_1", name: "Favorites", type: "favorites", treeItemIds: [], itemOrder: [] },
            ],
          },
        };
        const defaultDashboardId = ulid();
        const defaultDashboardName = "Homepage Dashboard";

        tileLayoutItems.value = defaultTiles;
        tileTreeViewConfigs.value = defaultTreeViewConfigs;
        currentDashboardId.value = defaultDashboardId;
        dashboardName.value = defaultDashboardName;

        // Initialize first tab with default data
        dashboardTabs.value = [
          {
            id: defaultDashboardId,
            name: defaultDashboardName,
            tiles: defaultTiles,
            treeViewConfigs: defaultTreeViewConfigs,
            isLocked: true,
          },
        ];
      }
    } catch (error) {
      notification.value = {
        show: true,
        type: "error",
        message: `Failed to load dashboards`,
        error: error,
      };

      // Create default dashboard on error
      const defaultTileId = 1;
      const defaultTiles = [
        {
          id: defaultTileId,
          header: "Favorite Templates",
          body: "",
          defaultPosition: {
            col: 1,
            colSpan: 2,
            rowSpan: 4,
          },
          resizable: true,
          reorderable: true,
        },
      ];
      const defaultTreeViewConfigs = {
        [defaultTileId]: {
          treeViews: [
            { id: "tv_1", name: "Favorites", type: "favorites", treeItemIds: [], itemOrder: [] },
          ],
        },
      };
      const defaultDashboardId = ulid();
      const defaultDashboardName = "Homepage Dashboard";

      tileLayoutItems.value = defaultTiles;
      tileTreeViewConfigs.value = defaultTreeViewConfigs;
      currentDashboardId.value = defaultDashboardId;
      dashboardName.value = defaultDashboardName;

      dashboardTabs.value = [
        {
          id: defaultDashboardId,
          name: defaultDashboardName,
          tiles: defaultTiles,
          treeViewConfigs: defaultTreeViewConfigs,
          isLocked: true,
        },
      ];
    } finally {
      isLoading.value = false;
    }
  };

  // Get current tab's isLocked state
  const getCurrentTabIsLocked = () => {
    const currentTab = dashboardTabs.value[selectedTabIndex.value];
    return currentTab?.isLocked ?? true;
  };

  // Toggle lock for current tab
  const handleScreenLockToggle = () => {
    const updatedTabs = [...dashboardTabs.value];
    const currentTab = updatedTabs[selectedTabIndex.value];
    if (currentTab) {
      currentTab.isLocked = !currentTab.isLocked;
      dashboardTabs.value = updatedTabs;
    }
  };

  const handleSaveTiles = useCallback(async () => {
    const currentTab = dashboardTabs.value[selectedTabIndex.value];
    if (currentTab?.isLocked) return;

    try {
      isLoading.value = true;
      const dashboardId = currentDashboardId.value || ulid();

      await homepageService.saveDashboard({
        id: dashboardId,
        name: dashboardName.value,
        tiles: tileLayoutItems.value,
        treeViewConfigs: tileTreeViewConfigs.value,
      });
      currentDashboardId.value = dashboardId;

      // Update current tab in dashboardTabs
      const updatedTabs = [...dashboardTabs.value];
      updatedTabs[selectedTabIndex.value] = {
        ...updatedTabs[selectedTabIndex.value],
        id: dashboardId,
        name: dashboardName.value,
        tiles: tileLayoutItems.value,
        treeViewConfigs: tileTreeViewConfigs.value,
      };
      dashboardTabs.value = updatedTabs;

      notification.value = {
        show: true,
        type: "success",
        message: `Dashboard saved successfully`,
      };
    } catch (error) {
      console.log(error);

      notification.value = {
        show: true,
        type: "error",
        message: `Failed to save dashboard`,
        error: error,
      };
    } finally {
      isLoading.value = false;
    }
  }, []);

  const handleDashboardNameClick = () => {
    if (!getCurrentTabIsLocked()) {
      isEditingName.value = true;
    }
  };

  const handleDashboardNameChange = (e) => {
    dashboardName.value = e.target.value;

    // Update current tab name
    const updatedTabs = [...dashboardTabs.value];
    updatedTabs[selectedTabIndex.value] = {
      ...updatedTabs[selectedTabIndex.value],
      name: e.target.value,
    };
    dashboardTabs.value = updatedTabs;
  };

  const handleDashboardNameBlur = useCallback(() => {
    isEditingName.value = false;
  }, []);

  const handleDashboardNameKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      isEditingName.value = false;
    }
  }, []);

  const findFirstAvailablePosition = () => {
    const columns = 8;
    const columnHeights = Array(columns).fill(0);

    tileLayoutItems.value.forEach((tile) => {
      const pos = tile.defaultPosition;
      const startCol = pos.col - 1;
      const colSpan = pos.colSpan || 1;
      const rowSpan = pos.rowSpan || 1;

      let maxHeightInRange = 0;
      for (let col = startCol; col < startCol + colSpan; col++) {
        if (columnHeights[col] > maxHeightInRange) {
          maxHeightInRange = columnHeights[col];
        }
      }

      const newHeight = maxHeightInRange + rowSpan;
      for (let col = startCol; col < startCol + colSpan; col++) {
        columnHeights[col] = newHeight;
      }
    });

    let minHeight = Math.min(...columnHeights);
    let targetCol = columnHeights.indexOf(minHeight);

    if (minHeight === 0 && targetCol === -1) {
      targetCol = 0;
    }

    return {
      col: targetCol + 1,
      colSpan: 1,
      rowSpan: 1,
    };
  };

  const getNextTileId = () => {
    if (tileLayoutItems.value.length === 0) return 1;
    const maxId = Math.max(...tileLayoutItems.value.map((tile) => tile.id));
    return maxId + 1;
  };

  const handleOpenAddTileDialog = () => {
    tileDialogMode.value = "add";
    editingTile.value = null;
    showTileDialog.value = true;
  };

  const handleOpenEditTileDialog = (tile) => {
    tileDialogMode.value = "edit";
    editingTile.value = tile;
    showTileDialog.value = true;
  };

  const handleCloseTileDialog = () => {
    showTileDialog.value = false;
    editingTile.value = null;
  };

  const handleConfirmTile = (tileName) => {
    if (tileDialogMode.value === "add") {
      const newTileId = getNextTileId();

      // Find placeholder position before removing it
      const placeholderPosition = findFirstAvailablePosition();

      const newTile = {
        id: newTileId,
        header: tileName,
        body: "",
        defaultPosition: placeholderPosition,
        resizable: !getCurrentTabIsLocked(),
        reorderable: !getCurrentTabIsLocked(),
      };

      // Initialize TreeView config for new tile
      tileTreeViewConfigs.value = {
        ...tileTreeViewConfigs.value,
        [newTileId]: { treeViews: [], itemOrder: {} },
      };

      tileLayoutItems.value = [...tileLayoutItems.value, newTile];
    } else if (tileDialogMode.value === "edit" && editingTile.value) {
      const updatedItems = tileLayoutItems.value.map((tile) =>
        tile.id === editingTile.value.id ? { ...tile, header: tileName } : tile
      );
      tileLayoutItems.value = updatedItems;
    }

    showTileDialog.value = false;
    editingTile.value = null;
  };

  const handleOpenDeleteTileDialog = (tileId, tileName) => {
    tileToDelete.value = { id: tileId, name: tileName };
    showDeleteTileDialog.value = true;
  };

  const handleCloseDeleteTileDialog = () => {
    showDeleteTileDialog.value = false;
    tileToDelete.value = null;
  };

  const handleConfirmDeleteTile = () => {
    if (tileToDelete.value) {
      tileLayoutItems.value = tileLayoutItems.value.filter(
        (tile) => tile.id !== tileToDelete.value.id
      );

      // Clean up TreeView config for deleted tile
      const remainingConfigs = { ...tileTreeViewConfigs.value };
      delete remainingConfigs[tileToDelete.value.id];
      tileTreeViewConfigs.value = remainingConfigs;

      showDeleteTileDialog.value = false;
      tileToDelete.value = null;
    }
  };

  // Tile Config Dialog handlers (for adding components to tiles)
  const handleOpenTileConfigDialog = (tileId) => {
    configuringTileId.value = tileId;
    showTileConfigDialog.value = true;
  };

  const handleCloseTileConfigDialog = () => {
    showTileConfigDialog.value = false;
    configuringTileId.value = null;
  };

  const handleAddComponent = (tileId, componentType) => {
    if (componentType === "treeView") {
      const currentConfig = tileTreeViewConfigs.value[tileId] || { treeViews: [] };
      const existingTreeViews = currentConfig.treeViews || [];
      const newTreeViewNumber = existingTreeViews.length + 1;
      const newTreeViewId = `tv_${ulid()}`;

      const newTreeView = {
        id: newTreeViewId,
        name: `New TreeView ${newTreeViewNumber}`,
        type: "custom", // Default type - empty custom TreeView to be configured
        treeItemIds: [],
        itemOrder: [],
      };

      tileTreeViewConfigs.value = {
        ...tileTreeViewConfigs.value,
        [tileId]: {
          ...currentConfig,
          treeViews: [...existingTreeViews, newTreeView],
        },
      };
    }
    showTileConfigDialog.value = false;
    configuringTileId.value = null;
  };

  // TreeView Config Dialog handlers (for configuring individual TreeViews)
  const [configuringTreeViewTileId, setConfiguringTreeViewTileId] = useState(null);

  const handleTreeViewConfigClick = (tileId, treeViewId) => {
    setConfiguringTreeViewTileId(tileId);
    configuringTreeViewId.value = treeViewId;
    showTreeViewConfigMenu.value = true;
  };

  const handleCloseTreeViewConfigDialog = () => {
    showTreeViewConfigMenu.value = false;
    configuringTreeViewId.value = null;
    setConfiguringTreeViewTileId(null);
  };

  const handleConfirmTreeViewConfig = (tileId, treeViewId, updates) => {
    const currentConfig = tileTreeViewConfigs.value[tileId];
    if (currentConfig) {
      const updatedTreeViews = currentConfig.treeViews.map((tv) =>
        tv.id === treeViewId ? { ...tv, ...updates } : tv
      );

      tileTreeViewConfigs.value = {
        ...tileTreeViewConfigs.value,
        [tileId]: {
          ...currentConfig,
          treeViews: updatedTreeViews,
        },
      };
    }
    showTreeViewConfigMenu.value = false;
    configuringTreeViewId.value = null;
    setConfiguringTreeViewTileId(null);
  };

  const handleDeleteTreeView = (tileId, treeViewId) => {
    const currentConfig = tileTreeViewConfigs.value[tileId];
    if (currentConfig) {
      const updatedTreeViews = currentConfig.treeViews.filter((tv) => tv.id !== treeViewId);

      tileTreeViewConfigs.value = {
        ...tileTreeViewConfigs.value,
        [tileId]: {
          ...currentConfig,
          treeViews: updatedTreeViews,
        },
      };
    }
    showTreeViewConfigMenu.value = false;
    configuringTreeViewId.value = null;
    setConfiguringTreeViewTileId(null);
  };

  // Get current TreeView config for the dialog
  const getCurrentTreeViewConfig = () => {
    if (!configuringTreeViewTileId || !configuringTreeViewId.value) return null;
    const tileConfig = tileTreeViewConfigs.value[configuringTreeViewTileId];
    if (!tileConfig) return null;
    return tileConfig.treeViews.find((tv) => tv.id === configuringTreeViewId.value);
  };

  // Tab handlers
  const handleTabSelect = (e) => {
    const newTabIndex = e.selected;
    const currentTab = dashboardTabs.value[selectedTabIndex.value];

    // Save current tab data before switching
    if (currentTab) {
      const updatedTabs = [...dashboardTabs.value];
      updatedTabs[selectedTabIndex.value] = {
        ...currentTab,
        tiles: tileLayoutItems.value,
        treeViewConfigs: tileTreeViewConfigs.value,
        name: dashboardName.value,
        id: currentDashboardId.value,
      };
      dashboardTabs.value = updatedTabs;
    }

    // Switch to new tab
    selectedTabIndex.value = newTabIndex;

    // Load new tab data
    const newTab = dashboardTabs.value[newTabIndex];
    if (newTab) {
      tileLayoutItems.value = newTab.tiles || [];
      tileTreeViewConfigs.value = newTab.treeViewConfigs || {};
      currentDashboardId.value = newTab.id;
      dashboardName.value = newTab.name || "New Dashboard";
    }
  };

  const handleAddNewTab = () => {
    const newTab = {
      id: ulid(),
      name: "New Dashboard",
      tiles: [],
      treeViewConfigs: {},
      isLocked: false, // New tabs start unlocked for editing
    };

    dashboardTabs.value = [...dashboardTabs.value, newTab];
    selectedTabIndex.value = dashboardTabs.value.length - 1;
    tileLayoutItems.value = [];
    tileTreeViewConfigs.value = {};
    currentDashboardId.value = newTab.id;
    dashboardName.value = newTab.name;
  };

  const handleOpenDeleteDashboardDialog = (tabIndex, e) => {
    e.stopPropagation();

    // Don't close if it's the only tab
    if (dashboardTabs.value.length <= 1) {
      return;
    }

    const tabToDelete = dashboardTabs.value[tabIndex];
    dashboardToDelete.value = { index: tabIndex, name: tabToDelete.name, id: tabToDelete.id };
    showDeleteDashboardDialog.value = true;
  };

  const handleCloseDeleteDashboardDialog = () => {
    showDeleteDashboardDialog.value = false;
    dashboardToDelete.value = null;
  };

  const handleConfirmDeleteDashboard = async () => {
    if (!dashboardToDelete.value) return;

    const tabIndex = dashboardToDelete.value.index;
    const tabId = dashboardToDelete.value.id;

    // Try to delete from backend if it has an ID
    if (tabId) {
      try {
        await homepageService.deleteDashboard(tabId);
      } catch (error) {
        console.error("Failed to delete dashboard from backend:", error);
        // Continue with local removal even if backend fails
      }
    }

    const updatedTabs = dashboardTabs.value.filter((_, index) => index !== tabIndex);
    dashboardTabs.value = updatedTabs;

    // Adjust selected index if needed
    if (selectedTabIndex.value >= updatedTabs.length) {
      selectedTabIndex.value = updatedTabs.length - 1;
    } else if (selectedTabIndex.value === tabIndex) {
      // If we closed the current tab, select the previous one (or first if it was the first)
      selectedTabIndex.value = Math.max(0, tabIndex - 1);
    }

    // Load the newly selected tab data
    const newSelectedTab = dashboardTabs.value[selectedTabIndex.value];
    if (newSelectedTab) {
      tileLayoutItems.value = newSelectedTab.tiles || [];
      tileTreeViewConfigs.value = newSelectedTab.treeViewConfigs || {};
      currentDashboardId.value = newSelectedTab.id;
      dashboardName.value = newSelectedTab.name || "New Dashboard";
    }

    showDeleteDashboardDialog.value = false;
    dashboardToDelete.value = null;
  };

  const handleReposition = (e) => {
    const updatedItems = tileLayoutItems.value.map((item, index) => ({
      ...item,
      defaultPosition: e.value[index],
    }));
    tileLayoutItems.value = updatedItems;
  };

  const renderTileHeader = (tile) => {
    const isLocked = getCurrentTabIsLocked();
    return (
      <div className="tile-header">
        <span>{tile.header}</span>
        {!isLocked && (
          <div className="tile-actions-container">
            <ColorChangingIcon
              icon={faPlusLarge}
              initialPrimaryColor="#00aeef"
              initialSecondaryColor="#2e5a87"
              initialSize={16}
              hoverSize={18}
              tooltip="Add Component"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenTileConfigDialog(tile.id);
              }}
            />
            <ColorChangingIcon
              icon={faPenLine}
              initialPrimaryColor="#F4A427"
              initialSecondaryColor="#d6562a"
              initialSize={16}
              hoverSize={18}
              tooltip="Edit Tile"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditTileDialog(tile);
              }}
            />
            <ColorChangingIcon
              icon={faTrashCan}
              initialPrimaryColor="#6e6e6e"
              initialSecondaryColor="#919191"
              initialSize={16}
              hoverSize={18}
              tooltip="Delete Tile"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteTileDialog(tile.id, tile.header);
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const renderPlaceholderTile = () => {
    const placeholderPosition = findFirstAvailablePosition();
    const isLocked = getCurrentTabIsLocked();
    return {
      id: "__placeholder_tile__",
      header: (
        <div className="placeholder-tile-header">
          <ColorChangingIcon
            icon={faPlusLarge}
            initialPrimaryColor="#00aeef"
            initialSecondaryColor="#2e5a87"
            initialSize={32}
            hoverSize={40}
            onClick={handleOpenAddTileDialog}
          />
        </div>
      ),
      body: null,
      defaultPosition: placeholderPosition,
      resizable: false,
      reorderable: !isLocked,
    };
  };

  const isCurrentTabLocked = getCurrentTabIsLocked();

  const preparedTileItems = tileLayoutItems.value.map((tile) => ({
    ...tile,
    header: renderTileHeader(tile),
    body: (
      <HomepageTreeView
        tileId={tile.id}
        onTreeViewConfigClick={handleTreeViewConfigClick}
        isLocked={isCurrentTabLocked}
      />
    ),
    resizable: !isCurrentTabLocked,
    reorderable: !isCurrentTabLocked,
  }));

  // Add placeholder tile when screen is unlocked
  if (!isCurrentTabLocked) {
    preparedTileItems.push(renderPlaceholderTile());
  }

  if (isLoading.value && tileLayoutItems.value.length === 0) {
    return <div className="homepage2-container">Loading...</div>;
  }

  return (
    <div className="homepage2-container">
      <TabStrip
        className="homepage-tabstrip"
        selected={selectedTabIndex.value}
        onSelect={handleTabSelect}
      >
        {dashboardTabs.value.map((tab, index) => (
          <TabStripTab
            key={tab.id || index}
            title={
              <span className="tab-title-container">
                <span className={`tab-name ${tab.isLocked ? "locked" : "unlocked"}`}>
                  {isEditingName.value && index === selectedTabIndex.value ? (
                    <input
                      type="text"
                      value={dashboardName.value}
                      onChange={handleDashboardNameChange}
                      onBlur={handleDashboardNameBlur}
                      onKeyDown={handleDashboardNameKeyDown}
                      autoFocus
                      className="tab-name-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    tab.name
                  )}
                </span>
                {index === selectedTabIndex.value && (
                  <>
                    <ColorChangingIcon
                      icon={tab.isLocked ? faLock : faLockOpen}
                      initialPrimaryColor={tab.isLocked ? "#4a90d9" : "#6e6e6e"}
                      initialSecondaryColor={tab.isLocked ? "#2e5a87" : "#919191"}
                      initialSize={12}
                      hoverSize={14}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleScreenLockToggle();
                      }}
                      tooltip={tab.isLocked ? "Unlock Dashboard" : "Lock Dashboard"}
                      style={{ marginLeft: "8px" }}
                    />
                    {!tab.isLocked && (
                      <>
                        <ColorChangingIcon
                          icon={faFloppyDisk}
                          initialPrimaryColor="#28a745"
                          initialSecondaryColor="#1e7e34"
                          initialSize={12}
                          hoverSize={14}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveTiles();
                          }}
                          tooltip="Save Dashboard"
                          style={{ marginLeft: "4px" }}
                        />
                        <ColorChangingIcon
                          icon={faPenLine}
                          initialPrimaryColor="#F4A427"
                          initialSecondaryColor="#d6562a"
                          initialSize={12}
                          hoverSize={14}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDashboardNameClick();
                          }}
                          tooltip="Edit Tab Name"
                          style={{ marginLeft: "4px" }}
                        />
                        {dashboardTabs.value.length > 1 && (
                          <ColorChangingIcon
                            icon={faTrashCan}
                            initialPrimaryColor="#6e6e6e"
                            initialSecondaryColor="#919191"
                            initialSize={12}
                            hoverSize={14}
                            onClick={(e) => handleOpenDeleteDashboardDialog(index, e)}
                            tooltip="Delete Dashboard"
                            style={{ marginLeft: "4px" }}
                          />
                        )}
                        <ColorChangingIcon
                          icon={faPlusLarge}
                          initialPrimaryColor="#00aeef"
                          initialSecondaryColor="#2e5a87"
                          initialSize={12}
                          hoverSize={14}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddNewTab();
                          }}
                          tooltip="Add New Tab"
                          style={{ marginLeft: "4px" }}
                        />
                      </>
                    )}
                  </>
                )}
              </span>
            }
          >
            <div className="homepage-content">
              <TileLayout
                columns={8}
                rowHeight={200}
                gap={{
                  rows: 10,
                  columns: 10,
                }}
                items={preparedTileItems}
                onReposition={handleReposition}
                dataItemKey={"id"}
              />
            </div>
          </TabStripTab>
        ))}
      </TabStrip>

      {showTileDialog.value && (
        <HomepageTileDialog
          onClose={handleCloseTileDialog}
          onConfirm={handleConfirmTile}
          mode={tileDialogMode.value}
          initialName={editingTile.value?.header || ""}
        />
      )}

      {showDeleteTileDialog.value && tileToDelete.value && (
        <ConfirmDeleteHomepageTileDialog
          handleCancel={handleCloseDeleteTileDialog}
          handleConfirm={handleConfirmDeleteTile}
          tileName={tileToDelete.value.name}
        />
      )}

      {showDeleteDashboardDialog.value && dashboardToDelete.value && (
        <ConfirmDeleteDashboardDialog
          handleCancel={handleCloseDeleteDashboardDialog}
          handleConfirm={handleConfirmDeleteDashboard}
          dashboardName={dashboardToDelete.value.name}
        />
      )}

      {showTileConfigDialog.value && configuringTileId.value && (
        <TileConfigDialog
          onClose={handleCloseTileConfigDialog}
          onAddComponent={handleAddComponent}
          tileId={configuringTileId.value}
        />
      )}

      {showTreeViewConfigMenu.value &&
        configuringTreeViewId.value &&
        getCurrentTreeViewConfig() && (
          <TreeViewConfigDialog
            onClose={handleCloseTreeViewConfigDialog}
            onConfirm={handleConfirmTreeViewConfig}
            onDelete={handleDeleteTreeView}
            treeViewConfig={getCurrentTreeViewConfig()}
            tileId={configuringTreeViewTileId}
          />
        )}
    </div>
  );
};

export default HomePage2;
